import type { Painel, PessoaOnline } from "../../services";
import { descreverAparelho } from "../aparelho";

export type Ponto = { pessoa: PessoaOnline; lat: number; lon: number; exato: boolean };

/** Quem tem posição do navegador aparece no ponto exato; os demais, na cidade do IP. */
export const pontosDoMapa = (pessoas: PessoaOnline[]): Ponto[] =>
  pessoas.flatMap((p): Ponto[] => {
    if (p.lat != null && p.lon != null) return [{ pessoa: p, lat: p.lat, lon: p.lon, exato: true }];
    if (p.ipLat != null && p.ipLon != null) {
      // várias pessoas na mesma cidade do IP: afasta um pouco para não empilhar
      const a = (p.userId * 137.5 * Math.PI) / 180;
      return [{ pessoa: p, lat: p.ipLat + 0.03 * Math.sin(a), lon: p.ipLon + 0.03 * Math.cos(a), exato: false }];
    }
    return [];
  });

/** 5400 -> "1 h 30 min"; 59 -> "1 min" (menos de 1 min arredonda para cima se houve uso). */
export const duracao = (segundos: number | string | null | undefined): string => {
  const s = Number(segundos ?? 0);
  if (!s) return "—";
  const min = Math.max(1, Math.round(s / 60));
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
};

/** Matriz 7 x 24 (domingo = 0) com o total de eventos e o maior valor, para pintar o mapa de calor. */
export const matrizDeCalor = (porHora: Painel["porHora"]) => {
  const m = Array.from({ length: 7 }, () => Array<number>(24).fill(0));
  let max = 0;
  for (const c of porHora) {
    const v = Number(c.total);
    if (c.dow >= 0 && c.dow < 7 && c.hora >= 0 && c.hora < 24) {
      m[c.dow][c.hora] += v;
      max = Math.max(max, m[c.dow][c.hora]);
    }
  }
  return { m, max };
};

/** Junta User-Agents parecidos ("Chrome · Windows") somando as pessoas. */
export const aparelhosAgrupados = (aparelhos: Painel["aparelhos"]) => {
  const soma = new Map<string, number>();
  for (const a of aparelhos) {
    const nome = descreverAparelho(a.userAgent);
    soma.set(nome, (soma.get(nome) ?? 0) + Number(a.usuarios));
  }
  return [...soma.entries()].map(([nome, usuarios]) => ({ nome, usuarios })).sort((x, y) => y.usuarios - x.usuarios);
};

/** "há 3 min", "há 2 h". */
export const haQuanto = (iso: string | null | undefined, agora = Date.now()): string => {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const min = Math.max(0, Math.round((agora - t) / 60000));
  if (min < 1) return "agora";
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  return h < 24 ? `há ${h} h` : `há ${Math.floor(h / 24)} d`;
};
